/* eslint-disable no-restricted-syntax */
import { RuleRunStatus, Rule, Maybe } from 'types/dt-graphql-apollo-v2';
import axios from 'axios';
import { notification } from 'antd';
import { compareISODates } from './misc';
import { DATA_VALIDATION_SERVICE_URL, GALACTICA_BACKEND } from 'utils/config';

type CustomObject = Object & { label: string };

export const getValidInstancesPercentage = (
  totalValidChecks: number = 0,
  totalInvalidChecks: number = 0
) => {
  if (totalValidChecks === 0 && totalInvalidChecks === 0) return 0;
  return (totalValidChecks / (totalInvalidChecks + totalValidChecks)) * 100;
};

export const compareRunDates = (run1: RuleRunStatus, run2: RuleRunStatus) => {
  if (compareISODates(run1.runAt || null, run2.runAt || null)) {
    return 1;
  }
  if (compareISODates(run2.runAt || null, run1.runAt || null)) {
    return -1;
  }
  return 0;
};

export const sortRunsByDate = (runs: RuleRunStatus[]) =>
  runs.slice().sort(compareRunDates);

const compareObjectsLabels = (object1: CustomObject, object2: CustomObject) => {
  if (!object1?.label) return 0;
  if (!object2?.label) return 0;
  if (object1?.label.toLowerCase() > object2?.label.toLowerCase()) {
    return 1;
  }
  if (object1?.label.toLowerCase() < object2?.label.toLowerCase()) {
    return -1;
  }
  return 0;
};

export const sortAlphabeticallyByLabel = (objects: CustomObject[]) =>
  objects.slice().sort(compareObjectsLabels);

export const checkRunHasValidData = (
  totalValid: Maybe<number> = 0,
  totalFailed: Maybe<number> = 0
) => (totalValid || 0) + (totalFailed || 0) !== 0;

export const getFailedInstances = async (
  runId: string,
  token: string | undefined,
  tenant: string | undefined,
  cluster: string | undefined
) => {
  try {
    const response = await axios.get<Blob>(
      `${DATA_VALIDATION_SERVICE_URL}/${runId}/failed-instances`,
      {
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          project: `${tenant}`,
          cluster: `${cluster}`,
        },
      }
    );
    return response.data;
  } catch (error: any) {
    notification.error({
      message: 'Failed to download failed-instances.json',
      description: error.toString(),
    });
  }
  return null;
};

/**
 * Get the schema type for a Rule, it can either be from rule.schemaType
 * or from the Ruleset connected to this Rule
 */
export const getSchemaTypeForRule = (rule?: Rule) =>
  rule?.rulesets?.items[0]?.schemaType ?? rule?.schemaType;

export const shouldRenewTokenForDV = (expiry: number | undefined) => {
  if (expiry && expiry < Date.now() / 1000 + 60 * 60) {
    // less than an hour
    return true;
  }
  return false;
};

export type ExportReportToDMMetadata = {
  ruleExternalId?: string;
  schemaType?: string;
  runAt?: string;
  errorMessage?: string;
  customerDefinedId?: string;
  collectionExternalId?: string;
};

export type ExportReportType = 'data-quality' | 'data-consistency';

export type ExportReportToDMPayload = {
  runIds: string[];
  exportType?: ExportReportType;
  metadata?: Record<string, ExportReportToDMMetadata>;
};

export const exportReportToDM = async (
  payload: ExportReportToDMPayload,
  token: string | undefined,
  tenant: string | undefined,
  cluster: string | undefined
): Promise<{ exported: number; errors: string[] }> => {
  const url = `${GALACTICA_BACKEND}/api/jobs/export-report-to-dm`;
  const response = await axios.post<{ exported: number; errors: string[] }>(
    url,
    payload,
    {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        project: `${tenant}`,
        cluster: `${cluster}`,
      },
    }
  );
  return response.data;
};
